import * as braze from '@braze/web-sdk';
import { Banner, InitializationOptions } from '@braze/web-sdk';
import { BrazeAttributeValue } from '@/models/analytics.model';
import { environment } from '../../environment';
import { apiUser } from '../api/apiUser';
import { localStorageUtils } from '../storage/localStorage';
import { IUser } from '../../models';
import { isUserAnonymous } from '../helpers/user';
import { logger } from '../logger/logger';
import {
  BRAZE_CONTAINERS_IDS,
  BRAZE_ALIAS_LABELS,
} from '../../constants/integrations/braze';
import {
  LOGGER_FEATURE,
  LOGGER_ACTION,
} from '../../constants/logger/logger-tags';

export class BrazeClient {
  private brazeInstance = braze;
  private brazeAliasStorageKey = 'brazeAliasUid';
  private brazeAliasesStorageKey = 'brazeExtraAliases';
  private isSdkInit = false;
  private webPushEventStorageKey = 'brazeWebPushEventSent';

  public async init(user: IUser, initWithEmail?: string): Promise<void> {
    try {
      const initOptions: InitializationOptions = {
        baseUrl: environment.brazeBaseUrl,
        sessionTimeoutInSeconds: 1,
        allowUserSuppliedJavascript: true,
        serviceWorkerLocation: '/app/service-worker.js',
        manageServiceWorkerExternally: true,
      };
      if (isUserAnonymous(user)) {
        initOptions.deviceId = user.uid;
      }

      this.brazeInstance.initialize(environment.brazeAppId, initOptions);

      // Enable automatic display of in-app messages (popups, modals, slideups)
      this.brazeInstance.automaticallyShowInAppMessages();

      // Subscribe to in-app messages for debugging
      this.brazeInstance.subscribeToInAppMessage(message => {
        logger.debug('Received in-app message from Braze', {
          tags: {
            feature: LOGGER_FEATURE.BRAZE,
            action: LOGGER_ACTION.RECEIVE,
          },
          extra: { message },
        });
      });

      if (user) {
        await this.identifyUserIfNeeded(user);
        if (isUserAnonymous(user)) {
          // if the uid was not already added as an alias
          if (this.needToAddAlias(user.uid)) {
            this.setAliasInStorage(user.uid);
            await this.syncAddAlias('anonymous_uid', user.uid);
            await this.mergeUserAliases();
          }
        }
      }

      if (initWithEmail) {
        this.setAliasesInStorage(BRAZE_ALIAS_LABELS.EMAIL, initWithEmail);
        await this.syncAddAlias(BRAZE_ALIAS_LABELS.EMAIL, initWithEmail);
        this.brazeInstance.getUser()?.setEmail(initWithEmail);
      }

      if (this.brazeInstance.isPushSupported()) {
        this.brazeInstance.requestPushPermission(
          () => {
            if (!localStorage.getItem(this.webPushEventStorageKey)) {
              this.firePermissionEnabledEvent('web_push');
              localStorage.setItem(this.webPushEventStorageKey, 'true');
            }
          },
          () => {
            localStorage.removeItem(this.webPushEventStorageKey);
          }
        );
      }

      this.brazeInstance.subscribeToBannersUpdates(
        (banners: Record<string, Banner | null>) => {
          Object.values(banners).forEach((banner: Banner | null) => {
            if (!banner) return;
            this.tryToInsertBanner(banner);
          });
        }
      );
      this.requestBanners();

      this.isSdkInit = true;
    } catch (error) {
      logger.error('Failed to initialize Braze SDK', error, {
        tags: { feature: LOGGER_FEATURE.BRAZE, action: LOGGER_ACTION.INIT },
        extra: {
          brazeAppId: environment.brazeAppId,
          baseUrl: environment.brazeBaseUrl,
          isAnonymous: isUserAnonymous(user),
        },
      });
    }
  }

  private tryToInsertBanner(banner: Banner): void {
    let count = 0;
    const intervalRef = setInterval(() => {
      count += 1;
      if (count >= 10) {
        clearInterval(intervalRef);
        return;
      }
      const element = document.getElementById(banner.placementId);
      if (element && !element.innerHTML) {
        clearInterval(intervalRef);
        this.brazeInstance.insertBanner(banner, element);
      }
    }, 500);
  }

  private async mergeUserAliases(): Promise<void> {
    const aliases = this.getAliasesFromStorage();
    if (!Array.isArray(aliases) || aliases.length === 0) {
      return;
    }
    // wait for the firebase user to be initialized
    setTimeout(async () => {
      const res = await apiUser.mergeUsersInBraze(aliases);
      if (res) {
        localStorageUtils.setItem(this.brazeAliasesStorageKey, []);
      }
    }, 2000);
  }

  public async identifyUserIfNeeded(user: IUser): Promise<void> {
    const brazeUser = this.brazeInstance.getUser();
    if (!isUserAnonymous(user) && brazeUser?.getUserId() !== user.uid) {
      this.brazeInstance.changeUser(user.uid);
      this.brazeInstance.getUser()?.setEmail(user.email);
    }
  }

  private setAliasesInStorage(alias: string, label: string): void {
    const aliases = this.getAliasesFromStorage();
    aliases.push({ alias, label });
    localStorageUtils.setItem(this.brazeAliasesStorageKey, aliases);
  }

  private getAliasesFromStorage(): { alias: string; label: string }[] {
    return localStorageUtils.getObjectItem<{ alias: string; label: string }[]>(
      this.brazeAliasesStorageKey,
      []
    );
  }

  private setAliasInStorage(uid: string): void {
    localStorage.setItem(this.brazeAliasStorageKey, uid);
  }

  private needToAddAlias(uid: string): boolean {
    const valFromStorage = localStorage.getItem(this.brazeAliasStorageKey);
    return !valFromStorage || valFromStorage !== uid;
  }

  private async syncAddAlias(alias: string, label: string): Promise<void> {
    this.brazeInstance.getUser()?.addAlias(alias, label);
    return new Promise<void>(resolve => {
      this.brazeInstance.requestImmediateDataFlush(() => {
        resolve();
      });
    });
  }

  private firePermissionEnabledEvent(permission: string): void {
    this.sendEvent('Permission_enabled', { Name: permission });
  }

  public sendEvent(name: string, args?: Record<string, any>): void {
    if (!this.isSdkInit) {
      return;
    }
    this.brazeInstance.logCustomEvent(name, args);
  }

  /**
   * Immediately flush all pending data to Braze servers
   */
  public async flushData(): Promise<void> {
    return new Promise<void>(resolve => {
      this.brazeInstance.requestImmediateDataFlush(() => {
        resolve();
      });
    });
  }

  /**
   * Request a refresh of all configured banners
   */
  public requestBanners(): void {
    this.brazeInstance.requestBannersRefresh(
      Object.values(BRAZE_CONTAINERS_IDS)
    );
  }

  /**
   * Reset the current session by flushing data and opening a new session
   */
  public resetSession(): void {
    this.brazeInstance.requestImmediateDataFlush(() => {
      this.brazeInstance.openSession();
    });
  }

  /**
   * Open a new Braze session
   */
  public openSession(): void {
    this.brazeInstance.openSession();
  }

  /**
   * Set a custom user attribute in Braze
   */
  public setCustomAttribute(name: string, value: BrazeAttributeValue): void {
    this.brazeInstance.getUser()?.setCustomUserAttribute(name, value);
  }
}

export const brazeUtils = new BrazeClient();
